"use client";

import { useCallback, useState } from "react";
import { useWorkspace } from "@/lib/workspace-context";
import { RefreshCw } from "lucide-react";
import ForceGraphCanvas from "./ForceGraphCanvas";
import type { GraphLink, GraphNode } from "@/stores/graph-store";

/**
 * Vista de grafo embebida en un pane del workspace.
 * Muestra los nodos del workspace y permite hover/click para navegar.
 */
export default function GraphView() {
  const { graphData, openFile } = useWorkspace();
  const [, setHoverNode] = useState<GraphNode | null>(null);
  const [highlightNodes, setHighlightNodes] = useState<Set<string>>(new Set());
  const [highlightLinks, setHighlightLinks] = useState<Set<GraphLink>>(new Set());
  const [recenterFn, setRecenterFn] = useState<(() => void) | null>(null);

  const handleRecenter = () => recenterFn?.();

  const handleNodeHover = useCallback(
    (node: GraphNode | null) => {
      const nextNodes = new Set<string>();
      const nextLinks = new Set<GraphLink>();
      if (node) {
        nextNodes.add(node.id);
        graphData.links.forEach((link) => {
          const sourceId = typeof link.source === "object" ? (link.source as GraphNode).id : link.source;
          const targetId = typeof link.target === "object" ? (link.target as GraphNode).id : link.target;
          if (sourceId === node.id || targetId === node.id) {
            nextLinks.add(link);
            nextNodes.add(sourceId === node.id ? targetId : sourceId);
          }
        });
      }
      setHoverNode(node);
      setHighlightNodes(nextNodes);
      setHighlightLinks(nextLinks);
    },
    [graphData.links],
  );

  const handleNodeClick = useCallback(
    (node: GraphNode) => {
      if (node && node.id) openFile(node.id);
    },
    [openFile],
  );

  return (
    <div className="w-full h-full min-h-[500px] flex-1 relative bg-background border border-border/30 rounded-2xl overflow-hidden shadow-inner flex flex-col">
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <h2 className="text-xs font-bold uppercase tracking-widest text-foreground/50">Red de Conocimiento</h2>
        <p className="text-[10px] text-muted-foreground">Vista interactiva de conexiones tácticas</p>
      </div>

      <div className="absolute top-4 right-4 z-10 flex gap-1.5">
        <button
          onClick={handleRecenter}
          className="p-1.5 rounded-lg liquid-glass text-muted-foreground hover:text-foreground transition-all border border-border/40"
          title="Centrar Grafo"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {graphData.nodes.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground/60 italic">
          No hay notas enlazadas para mostrar en el grafo
        </div>
      ) : (
        <ForceGraphCanvas
          graphData={graphData}
          highlightedNodeIds={highlightNodes}
          highlightedLinks={highlightLinks}
          onNodeClick={handleNodeClick}
          onNodeHover={handleNodeHover}
          onRecenter={setRecenterFn}
        />
      )}
    </div>
  );
}
